import type { IClientWebhook } from "@gen/app-query";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import { useCallback, useMemo } from "react";

import {
  EditableDataGrid,
  type EditableDataGridColDef,
} from "~/components/editable_data_grid";
import { HideableText } from "~/components/hideable_text";
import { clientWebhookSchema } from "~/entities/client_webhook";

interface WebhooksEditorProps {
  value: IClientWebhook[];
  onChange: (value: IClientWebhook[]) => void;
  isLoading: boolean;
  canDelete?: boolean;
  canAdd?: boolean;
  canEdit?: boolean;
  onSave: (value: IClientWebhook) => void;
  onDelete: (value: IClientWebhook) => void;
}

export function WebhooksEditor({
  value,
  onChange,
  isLoading,
  canDelete = true,
  canAdd = true,
  canEdit = true,
  onSave,
  onDelete,
}: WebhooksEditorProps) {
  const rows = value;

  const renderPasswordCell = useCallback((params: GridRenderCellParams) => {
    return (
      <HideableText
        value={params.value || ""}
        initiallyHidden={true}
        data-testid={`webhook-password-${params.id}`}
      />
    );
  }, []);

  const columns: EditableDataGridColDef<IClientWebhook>[] = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        minWidth: 150,
        editable: false,
        disableColumnMenu: true,
        sortable: false,
        resizable: false,
      },
      {
        field: "url",
        headerName: "URL",
        minWidth: 150,
        flex: 1,
        editable: true,
        disableColumnMenu: true,
        sortable: false,
        resizable: false,
      },
      {
        field: "username",
        headerName: "Username",
        minWidth: 150,
        editable: true,
        disableColumnMenu: true,
        sortable: false,
        resizable: false,
      },
      {
        field: "password",
        headerName: "Password",
        minWidth: 150,
        editable: true,
        disableColumnMenu: true,
        sortable: false,
        resizable: false,
        renderCell: renderPasswordCell,
      },
    ],
    [renderPasswordCell],
  );

  const createEmptyRow = useCallback((): IClientWebhook => {
    const newRow = {
      id: `tmp-${crypto.randomUUID()}`,
      created: new Date(),
      updated: new Date(),
      client_id: "",
      url: "",
      username: "",
      password: "",
    };
    return newRow;
  }, []);

  return (
    <EditableDataGrid<IClientWebhook>
      dataGridTestId="webhook-grid"
      addButtonDataTestId="create-webhook-button"
      columns={columns}
      rows={rows}
      onRowsChange={onChange}
      createEmptyRow={createEmptyRow}
      loading={isLoading}
      headingIconName="domain_verification"
      canAdd={canAdd}
      canEdit={canEdit}
      canDelete={canDelete}
      heading="Webhooks"
      addButtonLabel="Create Webhook"
      zodSchema={clientWebhookSchema}
      onSave={onSave}
      onDelete={onDelete}
      noRowsLabel="No Webhooks"
      deleteDialogHeading="Delete Webhook"
      getDeleteDialogMessage={(row) =>
        `Are you sure you want to delete Webhook ${row.id}?`
      }
      deleteDialogConfirmText="Yes, delete Webhook"
      deleteDialogCancelText="No, cancel"
    />
  );
}
